/**
 * Import Monitor — ERP Balance Mismatch.
 *
 * Schedule slots where Helix's balance (derived from GRN attribution) differs
 * from the ERP's own open qty for that delivery date. Only tenants whose
 * import captures the ERP balance return `enabled: true`.
 */
import api from './client';

/** Helix side of the comparison: helix_higher = Helix shows more still to receive. */
export type ErpBalanceMismatchDirection = 'helix_higher' | 'erp_higher';
/** Schedule date relative to the current delivery week (Monday start). */
export type ErpBalanceMismatchWindow = 'past_due' | 'this_week' | 'future';

export interface ErpBalanceMismatchRow {
  schedule_id: number;
  po_line_id: number;
  po_number: string;
  line_number: number;
  schedule_line_number: string | null;
  part_number: string | null;
  supplier_name: string | null;
  schedule_date: string | null;
  status: string;
  schedule_qty: number;
  /** Helix received qty (GRN attribution). */
  shipped_qty: number;
  /** Helix balance qty. */
  balance_qty: number;
  erp_balance_qty: number;
  /** balance_qty − erp_balance_qty. */
  difference: number;
  updated_at: string | null;
  /** When the import last wrote this slot's ERP balance. */
  erp_reported_at: string | null;
  /** A GRN reached Helix after the ERP last reported this line — the mismatch may just be timing. */
  awaiting_erp: boolean;
}

export interface ErpBalanceMismatchSummary {
  total: number;
  helix_higher: number;
  erp_higher: number;
}

export interface ErpBalanceMismatchResponse {
  enabled: boolean;
  /** Counts across the current filters, before the direction filter. Null when not enabled. */
  summary: ErpBalanceMismatchSummary | null;
  count: number;
  page: number;
  page_size: number;
  results: ErpBalanceMismatchRow[];
}

export interface ErpBalanceMismatchFilters {
  search?: string;
  direction?: ErpBalanceMismatchDirection;
  window?: ErpBalanceMismatchWindow;
  includeCancelled?: boolean;
}

function toParams(customerId: number, filters: ErpBalanceMismatchFilters): Record<string, string | number> {
  const params: Record<string, string | number> = { customer_id: customerId };
  if (filters.search) params['search'] = filters.search;
  if (filters.direction) params['direction'] = filters.direction;
  if (filters.window) params['window'] = filters.window;
  if (filters.includeCancelled) params['include_cancelled'] = 1;
  return params;
}

export const erpBalanceMismatchAPI = {
  /** One page of mismatched slots plus summary counts. */
  list: (
    customerId: number,
    filters: ErpBalanceMismatchFilters,
    page: number,
    pageSize: number,
  ): Promise<ErpBalanceMismatchResponse> =>
    api.get<ErpBalanceMismatchResponse>('/imports/erp-balance-mismatch/', {
      params: { ...toParams(customerId, filters), page, page_size: pageSize },
    }).then(r => r.data),

  /** Every mismatch matching the filters, as an .xlsx blob. */
  exportXlsx: (customerId: number, filters: ErpBalanceMismatchFilters): Promise<Blob> =>
    api.get('/imports/erp-balance-mismatch/export/', {
      params: toParams(customerId, filters),
      responseType: 'blob',
    }).then(r => r.data as Blob),
};
